import { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { LimitNote, Note, NotSavedNote, overLimit } from "@/components/ui/Note";
import {
  type Rename,
  oldNameWords,
  oldNamesIn,
  replaceOldName,
} from "@/lib/old-names";
import { TEXT_LIMITS } from "@/lib/text-limits";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Explainer } from "@/components/ui/Explainer";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { Check, Mic, Upload } from "lucide-react";
import { AudioCaptureControls } from "@/components/shared/AudioCaptureControls";
import { useAudioRecorder } from "@/hooks/use-audio-recorder";
import { useElapsedClock } from "@/hooks/use-elapsed-clock";
import { useSelfSavingDraft } from "@/hooks/use-debounced-save";
import { blank, joinWalkthrough } from "@/lib/figure-status";
import { cn } from "@/lib/utils";

export type WalkthroughClipKind = "recording" | "upload";

/** Where a clip's transcription stands, in the card's own vocabulary. A clip
 * stored before transcription existed stays plainly "stored"; "adopted" means
 * its words are already in the field; "silent" means it heard none. */
export type WalkthroughClipState =
  "stored" | "transcribing" | "transcribed" | "adopted" | "silent" | "failed";

export interface WalkthroughClip {
  fileId: string;
  kind: WalkthroughClipKind;
  filename: string;
  state: WalkthroughClipState;
  transcript: string | null;
  /** When the clip was last sent to be heard (ISO), so its wait is counted from then. */
  sentAt: string | null;
}

function CaptureHeader({
  recording,
  stream,
  onStart,
  onStop,
  onUpload,
}: {
  recording: boolean;
  stream: MediaStream | null;
  onStart: () => void;
  onStop: () => void;
  onUpload: (file: File) => void;
}) {
  return (
    <div className="mb-2 flex flex-wrap items-start gap-3">
      <CardTitle />
      <AudioCaptureControls
        recording={recording}
        stream={stream}
        idPrefix="define-walkthrough"
        onStart={onStart}
        onStop={onStop}
        onUpload={onUpload}
      />
    </div>
  );
}

function CardTitle() {
  return (
    <span className="flex min-w-0 flex-1 basis-[280px] items-center gap-2">
      <label htmlFor="walk-through">
        <MicroLabel>Walk me through this figure</MicroLabel>
      </label>
      <Explainer label="What is this for?" testId="walkthrough-why">
        This text will not appear in the draft. It is context for the assistant
        to draft the detailed description. No legal language is required.
      </Explainer>
    </span>
  );
}

/**
 * A clip on its way through transcription, with the time it has taken so far.
 * The service reports no fraction done, so the count is the progress there is:
 * a long clip takes minutes, and a still row in that time reads as hung.
 */
function Transcribing({ sentAt }: { sentAt: string | null }) {
  const clock = useElapsedClock(sentAt);
  return (
    <span className="flex flex-none items-center gap-2">
      <span
        aria-hidden
        className="h-1 w-16 animate-shimmer rounded-full bg-gradient-to-r from-border via-primary/50 to-border"
      />
      <span className="italic tabular-nums text-muted-foreground">
        {`Transcribing… ${clock}`}
      </span>
    </span>
  );
}

/** The right-hand side of one clip row: where its transcription stands. */
function ClipState({
  clip,
  onRetry,
}: {
  clip: WalkthroughClip;
  onRetry: (fileId: string) => void;
}) {
  if (clip.state === "transcribing")
    return <Transcribing sentAt={clip.sentAt} />;
  if (clip.state === "silent") {
    return (
      <span className="flex-none italic text-muted-foreground">
        No speech heard in this clip.
      </span>
    );
  }
  if (clip.state === "adopted") {
    return (
      <span className="flex flex-none items-center gap-1 text-muted-foreground">
        <Check aria-hidden className="size-3.5 text-primary" />
        Added to walk-through
      </span>
    );
  }
  if (clip.state === "failed") {
    return (
      <span className="flex flex-none items-center gap-2 text-destructive">
        Couldn't transcribe this clip.
        <Button
          variant="secondary"
          size="dense"
          type="button"
          onClick={() => {
            onRetry(clip.fileId);
          }}
        >
          Retry
        </Button>
      </span>
    );
  }
  return null;
}

function ClipList({
  clips,
  onRetry,
}: {
  clips: readonly WalkthroughClip[];
  onRetry: (fileId: string) => void;
}) {
  return (
    <ul
      data-testid="define-walkthrough-clips"
      aria-label="Walk-through clips"
      className={cn("space-y-1.5", clips.length > 0 && "mt-3")}
    >
      {clips.map((clip) => (
        <Panel
          as="li"
          shape="row"
          pad="tight"
          key={clip.fileId}
          className="flex animate-row-in items-center justify-between gap-3 border-border/70 bg-muted/30 py-1.5 text-[12px] text-muted-foreground"
        >
          <span className="flex min-w-0 items-center gap-2">
            {clip.kind === "recording" ? (
              <Mic aria-hidden className="size-3.5 flex-none" />
            ) : (
              <Upload aria-hidden className="size-3.5 flex-none" />
            )}
            <span className="truncate">{clip.filename}</span>
          </span>
          <ClipState clip={clip} onRetry={onRetry} />
        </Panel>
      ))}
    </ul>
  );
}

/** The field itself, with the arrival flash. */
function DraftField({
  draft,
  flash,
  locked,
  onDraft,
  onFlush,
}: {
  draft: string;
  flash: number;
  locked: boolean;
  onDraft: (next: string) => void;
  onFlush: () => void;
}) {
  return (
    <>
      <Textarea
        id="walk-through"
        rows={4}
        value={draft}
        placeholder="What this figure shows, in your own words…"
        onChange={(event) => {
          onDraft(event.target.value);
        }}
        onBlur={onFlush}
        readOnly={locked}
        className={cn(
          "min-h-[88px] resize-y text-[13px] leading-normal",
          flash > 0 && "animate-insert-flash",
        )}
      />
      <LimitNote
        length={draft.length}
        max={TEXT_LIMITS.figureWalkthrough}
        className="mt-1"
      />
    </>
  );
}

/** The arrival flash: armed per insert, rearmed once its animation has played. */
function useInsertFlash(): [number, () => void] {
  const [flash, setFlash] = useState(0);
  // A timer rather than animationend, which a hidden tab never fires.
  useEffect(() => {
    if (flash === 0) return;
    const played = setTimeout(() => {
      setFlash(0);
    }, 1300);
    return () => {
      clearTimeout(played);
    };
  }, [flash]);
  return [flash, () => setFlash((count) => count + 1)];
}

/**
 * Amend, and focus the field once the amend reopens it: the attorney asked for the words
 * back under their hands.
 */
function useAmendFocus(accepted: boolean, onAmend: () => void): () => void {
  const reopenedRef = useRef(false);
  useEffect(() => {
    if (!accepted && reopenedRef.current) {
      reopenedRef.current = false;
      document.getElementById("walk-through")?.focus();
    }
  }, [accepted]);
  return () => {
    reopenedRef.current = true;
    onAmend();
  };
}

/** What the line beside Confirm asks of the attorney before they press it. */
function confirmHint(empty: boolean, transcribing: boolean): string {
  if (transcribing) return "Wait for transcription to finish.";
  if (empty)
    return "Draft the description, then confirm it when you're happy with it.";
  return "Confirm the walk-through when you're happy with it.";
}

/**
 * Below the clips: the confirmation toggle. Unconfirmed, it offers the one
 * press that says "I'm happy with what is there"; confirmed, it stands as
 * the receipt with the one way back an explicit amend. Presentation only —
 * the owner records the verdict.
 */
function ConfirmRow({
  accepted,
  empty,
  transcribing,
  blocked,
  onConfirm,
  onAmend,
}: {
  accepted: boolean;
  empty: boolean;
  /** A clip's words are still on their way into the field. */
  transcribing: boolean;
  /** Nothing to confirm yet, or words still arriving: from the field, the microphone or a clip. */
  blocked: boolean;
  onConfirm: () => void;
  onAmend: () => void;
}) {
  if (accepted) {
    return (
      <div
        data-testid="define-walkthrough-confirmed"
        className="mt-3 flex flex-wrap items-center justify-end gap-2.5 animate-fade-in-up"
      >
        <span className="text-[11.5px] font-semibold text-primary">
          ✓ Walk-through confirmed
        </span>
        <Button
          variant="secondary"
          size="dense"
          type="button"
          onClick={onAmend}
        >
          ✎ Amend
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-3 flex flex-wrap items-center justify-end gap-2.5">
      <span className="text-[11.5px] text-muted-foreground">
        {confirmHint(empty, transcribing)}
      </span>
      <Button
        variant="confirm"
        size="dense"
        type="button"
        data-testid="define-walkthrough-confirm"
        disabled={blocked}
        onClick={onConfirm}
      >
        Confirm
      </Button>
    </div>
  );
}

/**
 * Fold freshly transcribed clips into the visible draft, exactly once each.
 *
 * The card is the field's single writer, so the words join whatever is on
 * screen (unsaved typing included) rather than the stored text underneath it.
 * The owner records the adoption in the same set the text lives in, which is
 * what keeps a reload from folding the same clip twice; the session ref only
 * keeps this effect from firing twice before that record lands.
 */
function useTranscriptArrivals(
  clips: readonly WalkthroughClip[],
  fold: (clipId: string, transcript: string) => void,
) {
  const foldedRef = useRef<Set<string>>(new Set());
  const foldRef = useRef(fold);
  foldRef.current = fold;
  useEffect(() => {
    for (const clip of clips) {
      if (clip.state !== "transcribed" || clip.transcript === null) continue;
      if (foldedRef.current.has(clip.fileId)) continue;
      foldedRef.current.add(clip.fileId);
      foldRef.current(clip.fileId, clip.transcript);
    }
  }, [clips]);
}

/** Why the walk-through is no longer confirmed, when a change other than Amend took it off. */
function LiftedNote({ lifted }: { lifted: string | null }) {
  if (lifted === null) return null;
  return (
    <Note tone="warning" className="mt-2">
      {`No longer confirmed: ${lifted}. Read the words and confirm again.`}
    </Note>
  );
}

/**
 * The components the words still name by an old name, each with the way to
 * put the name now in its place. Nothing changes until the attorney presses
 * Replace: the words are theirs. A confirmed walk-through is reopened first,
 * as Amend reopens it, so the attorney confirms the words again.
 */
function OldNames({
  draft,
  renames,
  reopen,
  onChange,
}: {
  draft: string;
  renames: readonly Rename[];
  /** Reopens a confirmed walk-through; null while it is open. */
  reopen: (() => void) | null;
  onChange: (draft: string) => void;
}) {
  const onReplace = (rename: Rename) => {
    reopen?.();
    onChange(replaceOldName(draft, rename));
  };
  const stale = oldNamesIn(draft, [...renames]);
  if (stale.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {stale.map((rename) => (
        <Note
          key={rename.old}
          tone="warning"
          className="flex items-center justify-between gap-2"
        >
          <span>{oldNameWords(rename)}</span>
          <Button
            type="button"
            size="dense"
            variant="secondary"
            aria-label={`Replace "${rename.old}"`}
            onClick={() => {
              onReplace(rename);
            }}
          >
            Replace
          </Button>
        </Note>
      ))}
    </div>
  );
}

/**
 * The attorney's own account of this figure, saved as it settles.
 *
 * Raw narration that must not appear in the draft: the brief description of
 * the drawings is generated from it later and confirmed in Review. Capture
 * sits on the title line; clips hang on the list between as rows that carry
 * their transcription state. A finished transcript folds itself into the
 * field — appended after whatever is typed, never displacing it — and the
 * field flashes so the eye lands where the words did. Confirm hands the
 * words on screen to the owner in one act, because what confirming means
 * is the owner's decision, not the field's.
 */
interface WalkthroughCardProps {
  walkthrough: string;
  clips: readonly WalkthroughClip[];
  onSave: (walkthrough: string) => Promise<void>;
  onUpload: (file: File) => void;
  onRecord: (file: File) => void;
  onRetryClip: (fileId: string) => void;
  onAdoptTranscript: (clipId: string, transcript: string) => void;
  /** The one stored confirmation bit: "I'm happy with what is there". */
  accepted: boolean;
  /** What took the confirmation off, when a change other than Amend did. */
  lifted?: string | null;
  /** Carries the words on screen: the accept writes them beside the bit. */
  onAccept: (walkthrough: string) => void;
  onAmend: () => void;
  /** The components renamed since: their old names are flagged in the words, never changed unasked. */
  renames?: readonly Rename[];
}

export function WalkthroughCard({
  walkthrough,
  clips,
  onSave,
  onUpload,
  onRecord,
  onRetryClip,
  onAdoptTranscript,
  accepted,
  lifted = null,
  onAccept,
  onAmend,
  renames = [],
}: WalkthroughCardProps) {
  const { draft, setDraft, flush, failed, retry } = useSelfSavingDraft(
    walkthrough,
    onSave,
    TEXT_LIMITS.figureWalkthrough,
  );
  const { recording, stream, start, stop } = useAudioRecorder(onRecord);
  const [flash, armFlash] = useInsertFlash();
  const reopen = useAmendFocus(accepted, onAmend);
  const empty = blank(draft);
  const transcribing = clips.some((clip) => clip.state === "transcribing");
  const blocked = empty || recording || transcribing;

  // The field as the folds see it. A render-time `draft` would be a snapshot:
  // two transcripts arriving in one pass would both join against it and the
  // second would erase the first. The ref carries each fold into the next.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useTranscriptArrivals(clips, (clipId, transcript) => {
    const joined = joinWalkthrough(draftRef.current, transcript);
    draftRef.current = joined;
    setDraft(joined);
    armFlash();
    onAdoptTranscript(clipId, transcript);
  });

  return (
    <Panel
      as="section"
      data-testid="define-walkthrough-card"
      data-empty={empty}
      attention={empty}
      className="mt-3.5 shadow-sm"
    >
      <CaptureHeader
        recording={recording}
        stream={stream}
        onStart={() => void start()}
        onStop={stop}
        onUpload={onUpload}
      />
      <DraftField
        draft={draft}
        flash={flash}
        locked={accepted}
        onDraft={setDraft}
        onFlush={flush}
      />
      <NotSavedNote failed={failed} onRetry={retry} className="mt-1" />
      <OldNames
        draft={draft}
        renames={renames}
        reopen={accepted ? reopen : null}
        onChange={setDraft}
      />
      <LiftedNote lifted={accepted ? null : lifted} />
      <ClipList clips={clips} onRetry={onRetryClip} />
      <ConfirmRow
        accepted={accepted}
        empty={empty}
        transcribing={transcribing}
        blocked={blocked}
        onConfirm={() => {
          if (blocked) return;
          if (overLimit(draft, TEXT_LIMITS.figureWalkthrough)) return;
          onAccept(draft);
        }}
        onAmend={reopen}
      />
    </Panel>
  );
}
